import { z } from 'zod';
import { id, isoDate, isoDateTime, money } from './common.js';
import {
  partRequestSchema,
  partSchema,
  serviceOrderWithDerivedSchema,
  vehicleFeatureSchema,
} from './entities.js';
import { CURRENCY, PART_DOC_TYPE, SERVICE_ORDER_STATUS } from './constants.js';

/* --------------------------- Compras (peças) --------------------------- */

/** Peça solicitada enriquecida com o contexto da OS/veículo (área de compras). */
export const partRequestWithContextSchema = partRequestSchema.extend({
  osId: id,
  osTitulo: z.string(),
  vehicleId: id,
  vehicleApelido: z.string().optional(),
});
export type PartRequestWithContext = z.infer<typeof partRequestWithContextSchema>;

/** Move uma peça já comprada de outra OS para esta. */
export const movePartRequestSchema = z.object({ fromOsId: id, prId: id });
export type MovePartRequestInput = z.infer<typeof movePartRequestSchema>;

/** Lança uma peça entregue no estoque (campos específicos do inventário). */
export const partToStockSchema = z.object({
  sku: z.string().optional(),
  localizacao: z.string().optional(),
  estoqueMinimo: z.number().int().nonnegative().optional(),
});
export type PartToStockInput = z.infer<typeof partToStockSchema>;

/* ----------------------------- Dashboard ----------------------------- */

export const dashboardSchema = z.object({
  geradoEm: isoDateTime,
  kpis: z.object({
    osAbertas: z.number().int().nonnegative(),
    carrosNaGaragem: z.number().int().nonnegative(),
    pecasAbaixoMinimo: z.number().int().nonnegative(),
    vencimentosProximos: z.number().int().nonnegative(),
  }),
  osPorStatus: z.record(z.enum(SERVICE_ORDER_STATUS), z.number().int().nonnegative()),
  osAbertas: z.array(serviceOrderWithDerivedSchema),
  carrosParados: z.array(
    z.object({
      vehicleId: z.string(),
      apelido: z.string(),
      modelo: z.string(),
      diasParado: z.number().nonnegative(),
      osTitulo: z.string().optional(),
    }),
  ),
  alertasVencimento: z.array(
    z.object({
      vehicleId: z.string(),
      apelido: z.string(),
      tipo: z.string(),
      label: z.string(),
      dataVencimento: isoDate,
      diasRestantes: z.number(),
      status: z.string(),
    }),
  ),
  preventivasVencendo: z.array(
    z.object({
      vehicleId: z.string(),
      apelido: z.string(),
      titulo: z.string(),
      proximaData: isoDate.optional(),
      proximoKm: z.number().optional(),
      status: z.string(),
    }),
  ),
});
export type Dashboard = z.infer<typeof dashboardSchema>;

/* ----------------------------- Lookups ------------------------------- */

export const expiringFeaturesSchema = z.object({
  items: z.array(vehicleFeatureSchema.extend({ diasRestantes: z.number() })),
});
export type ExpiringFeatures = z.infer<typeof expiringFeaturesSchema>;

/** Resposta do scanner de QR: peça resolvida pelo qrValue. */
export const partLookupSchema = partSchema;

/* ------------------------------- Fotos ------------------------------- */

export const presignPhotoRequestSchema = z.object({
  contentType: z.string().default('image/jpeg'),
  prefix: z.string().optional(),
  legenda: z.string().optional(),
});
export type PresignPhotoRequest = z.infer<typeof presignPhotoRequestSchema>;

export const setPasswordSchema = z.object({
  password: z.string().min(8, 'A senha deve ter ao menos 8 caracteres'),
});
export type SetPasswordInput = z.infer<typeof setPasswordSchema>;

export const presignPhotoResponseSchema = z.object({
  uploadUrl: z.string().url(),
  key: z.string(),
  publicUrl: z.string(),
});
export type PresignPhotoResponse = z.infer<typeof presignPhotoResponseSchema>;

/* --------------------------------- IA -------------------------------- */

export const aiQueryRequestSchema = z.object({
  pergunta: z.string().min(1),
});
export type AiQueryRequest = z.infer<typeof aiQueryRequestSchema>;

export const aiQueryResponseSchema = z.object({
  resposta: z.string(),
  fonte: z.enum(['mock', 'claude']),
  dadosConsultados: z.array(z.string()).default([]),
  modelo: z.string().optional(),
});
export type AiQueryResponse = z.infer<typeof aiQueryResponseSchema>;

export const aiDiagnoseRequestSchema = z.object({
  vehicleId: z.string().min(1),
  serviceOrderId: z.string().optional(),
  sintomas: z.string().optional(),
});
export type AiDiagnoseRequest = z.infer<typeof aiDiagnoseRequestSchema>;

export const aiDiagnoseResponseSchema = z.object({
  diagnostico: z.string(),
  proximosPassos: z.array(z.string()).default([]),
  fonte: z.enum(['mock', 'claude']),
  modelo: z.string().optional(),
});
export type AiDiagnoseResponse = z.infer<typeof aiDiagnoseResponseSchema>;

/* ---- IA: enriquecer peça a partir do link do produto ---- */

export const partLinkEnrichRequestSchema = z.object({
  link: z.string().url('Informe uma URL válida do produto'),
});
export type PartLinkEnrichRequest = z.infer<typeof partLinkEnrichRequestSchema>;

export const partLinkEnrichResponseSchema = z.object({
  titulo: z.string().optional(),
  descricao: z.string().optional(),
  valor: money.optional(),
  moeda: z.enum(CURRENCY).optional(),
  fotoUrl: z.string().optional(),
  fonte: z.enum(['mock', 'claude']),
  modelo: z.string().optional(),
});
export type PartLinkEnrichResponse = z.infer<typeof partLinkEnrichResponseSchema>;

/* ---- IA: prazo de entrega a partir do código de rastreio ---- */

export const trackShipmentRequestSchema = z.object({
  codigoRastreio: z.string().min(1, 'Informe o código de rastreio'),
});
export type TrackShipmentRequest = z.infer<typeof trackShipmentRequestSchema>;

export const trackShipmentResponseSchema = z.object({
  transportadora: z.string().optional(),
  prazoEntrega: isoDate.optional(),
  status: z.string().optional(),
  /** true = prazo estimado por SLA (sem rastreio em tempo real). */
  estimado: z.boolean().default(true),
  detalhe: z.string().optional(),
  fonte: z.enum(['mock', 'claude']),
  modelo: z.string().optional(),
});
export type TrackShipmentResponse = z.infer<typeof trackShipmentResponseSchema>;

/* ---- IA: extrair OrderID/frete/valores de uma invoice (documento) ---- */

export const invoiceExtractRequestSchema = z.object({
  /** Conteúdo do arquivo em base64 (sem o prefixo `data:...;base64,`). */
  data: z.string().min(1).max(6_500_000, 'Arquivo grande demais para leitura automática'),
  /** MIME type: application/pdf ou image/(png|jpeg|gif|webp). */
  mediaType: z.string().min(1),
  nome: z.string().optional(),
});
export type InvoiceExtractRequest = z.infer<typeof invoiceExtractRequestSchema>;

export const invoiceExtractResponseSchema = z.object({
  /** Número do pedido na loja (Order ID / Order # / Invoice #). */
  orderId: z.string().optional(),
  fornecedor: z.string().optional(),
  /** Subtotal dos itens (sem frete/impostos), na moeda `moeda`. */
  valor: money.optional(),
  /** Frete/shipping, na moeda `moeda`. */
  frete: money.optional(),
  /** Total geral pago, na moeda `moeda`. */
  valorTotal: money.optional(),
  moeda: z.enum(CURRENCY).optional(),
  codigoRastreio: z.string().optional(),
  itens: z.array(z.string()).default([]),
  fonte: z.enum(['mock', 'claude']),
  modelo: z.string().optional(),
});
export type InvoiceExtractResponse = z.infer<typeof invoiceExtractResponseSchema>;

/* ---- IA: casar uma invoice/NF com as peças já cadastradas ---- */

/** Peça candidata enviada ao matcher (resumo do que está cadastrado). */
export const invoiceMatchCandidateSchema = z.object({
  id,
  osId: id,
  titulo: z.string().optional(),
  orderId: z.string().optional(),
});
export type InvoiceMatchCandidate = z.infer<typeof invoiceMatchCandidateSchema>;

/** Item do documento, já casado (ou não) com uma peça cadastrada. */
export const invoiceMatchItemSchema = z.object({
  descricao: z.string(),
  valor: money.optional(),
  quantidade: z.number().int().positive().optional(),
  /** Peça casada (se houver). */
  partId: id.optional(),
  osId: id.optional(),
  partTitulo: z.string().optional(),
  confianca: z.enum(['alta', 'media', 'baixa', 'nenhuma']),
  motivo: z.string().optional(),
});
export type InvoiceMatchItem = z.infer<typeof invoiceMatchItemSchema>;

/** Resposta do matcher: dados do documento + itens casados às peças. */
export const invoiceMatchResponseSchema = z.object({
  tipoDoc: z.enum(PART_DOC_TYPE),
  orderId: z.string().optional(),
  fornecedor: z.string().optional(),
  valor: money.optional(),
  frete: money.optional(),
  valorTotal: money.optional(),
  moeda: z.enum(CURRENCY).optional(),
  codigoRastreio: z.string().optional(),
  itens: z.array(invoiceMatchItemSchema).default([]),
  fonte: z.enum(['mock', 'claude']),
  modelo: z.string().optional(),
});
export type InvoiceMatchResponse = z.infer<typeof invoiceMatchResponseSchema>;

/* ----------------------- Caixa de e-mails (Compras) -------------------- */

export const recentEmailSchema = z.object({
  subject: z.string(),
  from: z.string(),
  date: z.string().optional(),
  messageId: z.string().optional(),
  /** Link p/ abrir o e-mail direto no Gmail (busca por rfc822msgid). */
  gmailUrl: z.string(),
});
export type RecentEmail = z.infer<typeof recentEmailSchema>;

export const recentEmailsResponseSchema = z.object({
  items: z.array(recentEmailSchema),
  /** false = caixa de e-mail não configurada (sem App Password). */
  configured: z.boolean(),
});
export type RecentEmailsResponse = z.infer<typeof recentEmailsResponseSchema>;
